export function isInteger(value: any): value is number {
  return (
    typeof value === 'number' && isFinite(value) && Math.floor(value) === value
  );
}

export function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

// Please be aware that guid values might be considered as numbers when parsing the value
export function isNumber(value: any): value is number {
  return !isNaN(toInteger(value));
}

/**
 * Returns `0` for a non-numeric id (GUID or composite string), or the original value for
 * a real numeric id. The backend treats `0` as "no database id yet" and inserts a new row;
 * a positive id triggers an update of the existing row. Uses `+id` (not `parseInt` /
 * `isNumber`) so a GUID that starts with digits is still treated as non-numeric.
 */
export function numericIdOrZero(id: string | number): number {
  return isNaN(+id) ? 0 : +id;
}

export function numericIdOrNull(id: string | number): number | null {
  return id == null || isNaN(+id) ? null : +id;
}

/**
 * Whether Angular's `number` pipe can format the value. It throws on a string that
 * isn't wholly a number, such as `'12abc'` or `' '`.
 */
export function isNumericValue(value: unknown): boolean {
  return typeof value === 'number'
    ? isFinite(value)
    : typeof value === 'string' && !isNaN(Number(value) - parseFloat(value));
}

export function padNumber(value: number): string {
  if (isNumber(value)) {
    return `0${value}`.slice(-2);
  } else {
    return '';
  }
}

export function getFirstDigit(x: number): number {
  while (x > 9) {
    x /= 10;
  }
  return x;
}

export function getDecimalCount(x: number): number {
  return Math.floor(x) === x ? 0 : x.toString().split('.')[1]?.length || 0;
}

export function getFixedLength(value: number, index: number): string {
  // 1e8 is enough for working with 8 digits (100000000)
  return value == null ? '' : (1e4 + '' + value).slice(-index);
}

export function isDecimalNumber(numberToCheck: number): boolean {
  return (
    numberToCheck.toString().includes('.') ||
    numberToCheck.toString().includes('e-')
  );
}

export function removeTrailingZeroDigits(
  numberToConvert: number,
  decimalPrecision: number
): string {
  const [integerPart, fractionPart] = numberToConvert
    .toFixed(decimalPrecision)
    .split('.');

  // A precision of 0 leaves no fraction at all, and one that strips to nothing
  // would leave a dangling separator — "5." is not a number anything can read
  // back.
  const fraction = (fractionPart ?? '').replace(/0+$/, '');

  return fraction === '' ? integerPart : `${integerPart}.${fraction}`;
}
